import { inject, injectable } from "inversify";
import type { GetCountryRegexResponseType } from "@whatslly/types/GetCountryRegexResponse";
import { COUNTRIES } from "@whatslly/commons/phoneUtils/countries";
import { ConversationFilterService } from "./conversationFilterService";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { contentBackend } from "@whatslly/commons/contentBackend";
import { backgroundClient } from "@whatslly/commons/background/client";

@injectable()
export class RecommendedCountryCodeService {
  private logger: FrontLogger;

  constructor(
    @inject(ConversationFilterService) private readonly conversationFilterService: ConversationFilterService,
    @inject(FrontLogger) private readonly frontLogger: FrontLogger,
    @inject(InjectStorage) private readonly storage: IStorage
  ) {
    this.logger = this.frontLogger.getLogger("recommended-country-code-service");
  }

  init() {
    const unsubscribe = contentBackend.on(ContentCommands.CONTENT_READY, async () => {
      unsubscribe();
      try {
        await this.setRecommendedCountryCodes();
      } catch (e) {
        this.logger.error({ err: e as Error }, "Error setting recommended country codes");
      }
    });
  }

  private async setRecommendedCountryCodes() {
    const countryCodesData = await this.storage.get(STORAGE.COUNTRY_CODES_KEY);
    if (countryCodesData?.success && countryCodesData?.method === "ip") {
      const recommendedCountryCodes = (await this.conversationFilterService.getCountryCodes()) ?? [];
      const countries = Array.from(new Set([...recommendedCountryCodes.map((c) => c.countryCode), ...countryCodesData.codes.map(([code]) => code)]))
        .map((code) => COUNTRIES.find((c) => c.code === code)?.country)
        .filter(Boolean)
        .slice(0, 3);
      const data = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetCountryRegexResponseType>(BgCommands.HTTP_REQUEST, {
        urlSuffix: `/codes/v2${countries.length > 0 ? `?countries=${countries.join(",")}` : ""}`,
        method: "GET",
      });
      if (!data.success) {
        return;
      }
      // TODO: CODES_KEY and REGEX_KEY are deprecated and should be removed in the future
      await this.storage.set({
        [STORAGE.COUNTRY_CODES_KEY]: data,
        [STORAGE.REGEX_KEY]: data.regex,
        [STORAGE.CODES_KEY]: data.codes as [string, string][],
      });
    }
  }
}
